import React, { useEffect } from 'react';
import AudioPlayer from './AudioPlayer';
import AudioPlayerPlaylistPanel from './AudioPlayerPlaylistPanel';
import AudioPlayerSessionLayout from './AudioPlayerSessionLayout';
import { useUrlAudioPlaylist } from './useUrlAudioPlaylist';
import { useWebStreamMetadata } from './useWebStreamMetadata';
import ExpandableText from './ExpandableText';

export default function StreamAudioSession({
  streamUrl,
  theme = 'auto',
  title = 'Web stream',
  bootstrapTracks = null,
  bootstrapMeta = null,
}) {
  const playlist = useUrlAudioPlaylist(streamUrl, title, {
    bootstrapTracks: bootstrapTracks || undefined,
    bootstrapMeta: bootstrapMeta || undefined,
  });
  const { meta, loading: metaLoading } = useWebStreamMetadata(playlist.currentTrack?.url, {
    enabled: Boolean(playlist.currentTrack?.url),
  });

  useEffect(() => {
    if (!meta?.songTitle && !meta?.title) return;
    const label = meta.songTitle || meta.title;
    playlist.setTracks((prev) =>
      prev.map((t) =>
        t.trackId === playlist.currentTrackId && t.source === 'url'
          ? { ...t, title: label, streamMetaLine: meta.subtitle || '' }
          : t
      )
    );
  }, [meta?.songTitle, meta?.title, meta?.subtitle, playlist.currentTrackId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!playlist.currentTrack?.url) {
    return null;
  }

  const playerTitle = meta?.title || playlist.currentTrack.title;
  const playerSubtitle = meta?.subtitle || playlist.currentTrack.url;
  const playerArtist = meta?.artist || '';

  return (
    <AudioPlayerSessionLayout
      layoutContext="page"
      playlistResetKey={playlist.currentTrackId}
      player={
        <div className="audio-player-page__stream-unit">
          {metaLoading && !meta ? (
            <p className="audio-player__message audio-player-page__stream-unit-status" role="status">
              Loading stream info…
            </p>
          ) : null}
          {meta ? (
            <dl className="audio-player__stream-details audio-player__stream-details--attached">
              {meta.stationName ? (
                <>
                  <dt>Station</dt>
                  <dd className="audio-player__stream-details-station">
                    <ExpandableText text={meta.stationName} maxLines={2} />
                    {meta.stationUrl ? (
                      <a className="audio-player__stream-details-link" href={meta.stationUrl}>
                        Website
                      </a>
                    ) : null}
                  </dd>
                </>
              ) : null}
              {meta.nowPlaying?.raw ? (
                <>
                  <dt>Now playing</dt>
                  <dd>
                    <ExpandableText text={meta.nowPlaying.raw} maxLines={2} />
                  </dd>
                </>
              ) : null}
              {meta.genre ? (
                <>
                  <dt>Genre</dt>
                  <dd>{meta.genre}</dd>
                </>
              ) : null}
              {meta.description ? (
                <>
                  <dt>About</dt>
                  <dd>
                    <ExpandableText text={meta.description} maxLines={3} />
                  </dd>
                </>
              ) : null}
              {meta.bitrate ? (
                <>
                  <dt>Bitrate</dt>
                  <dd>{meta.bitrate} kbps</dd>
                </>
              ) : null}
            </dl>
          ) : null}
          <AudioPlayer
            key={playlist.currentTrackId}
            className="audio-player--in-unit"
            theme={theme}
            src={playlist.currentTrack.url}
            title={playerTitle}
            subtitle={playerSubtitle}
            artist={playerArtist}
            artworkUrl={meta?.artworkUrl || ''}
            offlineKey={`url:${playlist.currentTrack.url}`}
            allowOffline
            onQueueAdvance={playlist.advanceTrack}
            queueHasNextTrack={playlist.hasNextTrackInQueue}
            equalizerGains={playlist.eqGains}
            onEqualizerGainsChange={playlist.setEqGains}
          />
        </div>
      }
      playlist={
        <AudioPlayerPlaylistPanel
          playlistName={playlist.playlistName}
          onPlaylistNameChange={playlist.setPlaylistName}
          tracks={playlist.tracks}
          currentTrackId={playlist.currentTrackId}
          shuffle={playlist.shuffle}
          onShuffleChange={playlist.setShuffle}
          repeat={playlist.repeat}
          onRepeatChange={playlist.setRepeat}
          onAddCurrentTrack={playlist.addCurrentTrack}
          addCurrentLabel="Add current stream"
          onSelectTrack={playlist.setCurrentTrackId}
          onRemoveTrack={(trackId) => {
            playlist.setTracks((prev) => {
              const next = prev.filter((t) => t.trackId !== trackId);
              if (playlist.currentTrackId === trackId) {
                playlist.setCurrentTrackId(next[0]?.trackId || '');
              }
              return next;
            });
          }}
          onLoadTracks={(loaded, meta) => playlist.loadTracks(loaded, meta)}
          linkedSavedPlaylistId={bootstrapMeta?.id}
          eqGains={playlist.eqGains}
          statusMessage={playlist.status}
          onStatusMessage={playlist.setStatus}
        />
      }
    />
  );
}
